import React, { ChangeEvent, useEffect, useState } from "react";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  Switch,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useStyles from "./styles";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store";
import {
  FilteredStoresState,
  StoresState
} from "../../../../store/stores/types";
import {
  thunkSaveFilteredStores,
  thunkFetchStores,
  thunkFetchFilteredStores
} from "../../../../store/stores/actions";
import { Store } from "../../../../models/Store";
import { SettingsChildrenProps } from "./SettingsDialog";
import { useI18n } from "../../../../hooks/useI18n";
import { useLoadIfEmpty } from "../../../../hooks/useLoadIfEmpty";

interface StoreFilter {
  store: Store;
  selected: boolean;
}

const initialState: StoreFilter[] = [];

export const StoreSettings = ({ onSuccess }: SettingsChildrenProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useI18n();

  const storesState = useSelector<AppState, StoresState>(s => s.stores);
  const filteredStoresState = useSelector<AppState, FilteredStoresState>(
    s => s.filteredStores
  );
  const [state, setState] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  useLoadIfEmpty(!storesState?.stores?.length, thunkFetchStores);
  useLoadIfEmpty(filteredStoresState.isLoading, thunkFetchFilteredStores);

  const confirm = () => {
    setIsLoading(true);
    let filteredStores = state.reduce((acc: Store[], curr) => {
      if (curr.selected) {
        acc.push(curr.store);
      }
      return acc;
    }, []);

    if (filteredStores.length === 0) {
      filteredStores = state.reduce((acc: Store[], curr) => {
        acc.push(curr.store);
        return acc;
      }, []);
    }

    dispatch(thunkSaveFilteredStores(filteredStores));
    setIsLoading(false);
    onSuccess();
  };

  const handleFilterChange = (event: ChangeEvent<{}>, filter: StoreFilter) => {
    const updatedState = state.map(sf => {
      if (sf.store.id === filter.store.id) {
        return { ...sf, selected: filter.selected };
      }
      return sf;
    });
    setState(updatedState);
  };

  useEffect(() => {
    let storeFilters = [];
    if (!filteredStoresState.stores.length) {
      storeFilters = storesState.stores.map(s => {
        return { selected: true, store: s };
      });
    } else {
      storeFilters = storesState.stores.map(s => {
        return {
          store: s,
          selected: !!filteredStoresState.stores.find(fs => fs.id === s.id)
        };
      });
    }
    setState(storeFilters);
  }, [storesState, filteredStoresState]);

  return (
    <>
      <DialogContent>
        <Container>
          <Grid item container className={classes.center}>
            <Box>
              <Typography variant={"body2"} color={"textSecondary"}>
                Select the stores you want to see on{" "}
                <Link href={"/"}>Overview</Link>
              </Typography>
              <Typography variant={"caption"} color={"textSecondary"}>
                *Disabling all stores is the same as selecting all
              </Typography>
            </Box>
            {[...state].map((storeFilter, index) => (
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={1}
                key={index}
              >
                <div className={classes.flex}>
                  <Typography color="textPrimary" style={{ flexGrow: 1 }}>
                    {storeFilter.store.name}
                  </Typography>
                </div>
                <div className={classes.flex}>
                  <Switch
                    checked={storeFilter.selected}
                    onChange={(e, selected) =>
                      handleFilterChange(e, {
                        store: storeFilter.store,
                        selected: selected
                      })
                    }
                    name={`all-on-${index}`}
                    inputProps={{
                      "aria-label": t("common.messages.secondaryCheckbox")
                    }}
                    color={"primary"}
                  />
                </div>
              </Box>
            ))}
          </Grid>
        </Container>
      </DialogContent>
      <DialogActions style={{ padding: 0 }}>
        <Container>
          <Box pr={3} pb={1} mt={2} display={"flex"} justifyContent={"end"}>
            <Button
              onClick={confirm}
              color="primary"
              autoFocus
              variant={"contained"}
              disabled={isLoading}
            >
              Save
            </Button>
          </Box>
        </Container>
      </DialogActions>
    </>
  );
};
